import { formatUsdc, USDC_DECIMALS } from "@breakout/sdk"
import { TRADING } from "@breakout/shared/constants"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "@breakout/ui/components/Dialog"
import { useToast } from "@breakout/ui/components/Toast"
import { AlertTriangle, Loader2 } from "lucide-react"
import { useCallback, useState } from "react"
import { getAddress, isAddress } from "viem"
import { useWithdraw } from "../../hooks/wallet/useWithdraw"
import { Sentry } from "../../lib/sentry"

interface WithdrawModalProps {
	isOpen: boolean
	onClose: () => void
	balance: bigint
}

export function WithdrawModal({ isOpen, onClose, balance }: WithdrawModalProps) {
	const { showToast } = useToast()
	const { mutateAsync: withdraw, status: withdrawStatus, isPending, reset } = useWithdraw()

	const [recipientAddress, setRecipientAddress] = useState("")
	const [amount, setAmount] = useState("")

	const balanceNumber = Number(balance) / 10 ** USDC_DECIMALS
	const formattedBalance = formatUsdc(balanceNumber, { symbol: false })

	// Validation
	const trimmedAddress = recipientAddress.trim()
	const isValidAddress = trimmedAddress.length > 0 && isAddress(trimmedAddress)
	const parsedAmount = Number.parseFloat(amount) || 0
	const isValidAmount = parsedAmount > 0 && parsedAmount <= balanceNumber
	const isBelowMinimum = parsedAmount > 0 && parsedAmount < TRADING.MIN_BUY_AMOUNT
	const canSubmit = isValidAddress && isValidAmount && !isBelowMinimum && !isPending

	const handleAmountChange = useCallback((value: string) => {
		if (value === "" || /^\d*\.?\d*$/.test(value)) {
			setAmount(value)
		}
	}, [])

	const handleMaxClick = useCallback(() => {
		setAmount(balanceNumber.toString())
	}, [balanceNumber])

	const handleClose = useCallback(() => {
		if (isPending) {
			return
		}
		onClose()
		// Reset state after close animation
		setTimeout(() => {
			setRecipientAddress("")
			setAmount("")
			reset()
		}, 200)
	}, [onClose, isPending, reset])

	const handleWithdraw = useCallback(async () => {
		if (!canSubmit) {
			return
		}

		try {
			const recipient = getAddress(trimmedAddress)
			await withdraw({ recipient, amount: parsedAmount })
			showToast("Withdrawal successful!", "success")
			handleClose()
		} catch (error) {
			console.error("[Withdraw] Error:", error)
			Sentry.captureException(error, {
				tags: { operation: "withdraw", domain: "wallet" },
				extra: { recipient: trimmedAddress, amount: parsedAmount },
			})
			const message = error instanceof Error ? error.message : "Withdrawal failed"
			showToast(message, "error")
		}
	}, [canSubmit, trimmedAddress, parsedAmount, withdraw, showToast, handleClose])

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				handleClose()
			}
		},
		[handleClose],
	)

	// Get status message
	const getStatusMessage = () => {
		switch (withdrawStatus) {
			case "signing":
				return "Signing..."
			case "processing":
				return "Processing..."
			default:
				return "Withdraw"
		}
	}

	return (
		<Dialog onOpenChange={handleOpenChange} open={isOpen}>
			<DialogContent className="max-w-sm">
				<div className="space-y-5">
					{/* Header */}
					<div>
						<DialogTitle className="font-bold font-display text-xl">Withdraw USDC</DialogTitle>
						<DialogDescription className="mt-1 text-sm text-text-secondary">
							Send USDC to an external wallet
						</DialogDescription>
					</div>

					{/* Warning */}
					<div className="flex items-start gap-3 rounded-xl bg-amber-500/10 p-3 ring-1 ring-amber-500/20">
						<AlertTriangle aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-amber-500" />
						<p className="text-pretty text-amber-600 text-sm dark:text-amber-400">
							Make sure to send funds on <span className="font-semibold">Base</span>. Sending to the
							wrong network may result in permanent loss.
						</p>
					</div>

					{/* Address Input */}
					<div className="space-y-2">
						<label
							className="block font-medium text-sm text-text-secondary"
							htmlFor="withdraw-address"
						>
							Recipient Address
						</label>
						<input
							autoComplete="off"
							className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-3 text-text-primary transition-colors placeholder:text-text-muted focus:border-breakout-brand focus:outline-none focus:ring-1 focus:ring-breakout-brand"
							disabled={isPending}
							id="withdraw-address"
							onChange={e => setRecipientAddress(e.target.value)}
							placeholder="0x..."
							spellCheck={false}
							type="text"
							value={recipientAddress}
						/>
						{trimmedAddress.length > 0 && !isValidAddress && (
							<p className="text-red-500 text-sm">Please enter a valid Ethereum address</p>
						)}
					</div>

					{/* Amount Input */}
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<label
								className="block font-medium text-sm text-text-secondary"
								htmlFor="withdraw-amount"
							>
								Amount
							</label>
							<span className="text-sm text-text-muted">Balance: ${formattedBalance}</span>
						</div>
						<div className="relative">
							<span className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 font-medium text-text-muted">
								$
							</span>
							<input
								autoComplete="off"
								className="w-full rounded-xl border border-border bg-bg-secondary py-3 pr-16 pl-8 text-text-primary transition-colors placeholder:text-text-muted focus:border-breakout-brand focus:outline-none focus:ring-1 focus:ring-breakout-brand"
								disabled={isPending}
								id="withdraw-amount"
								inputMode="decimal"
								onChange={e => handleAmountChange(e.target.value)}
								placeholder="0.00"
								type="text"
								value={amount}
							/>
							<button
								className="absolute top-1/2 right-3 -translate-y-1/2 rounded-lg bg-bg-tertiary px-2 py-1 font-medium text-breakout-brand text-xs transition-colors hover:bg-bg-tertiary/80"
								disabled={isPending}
								onClick={handleMaxClick}
								type="button"
							>
								MAX
							</button>
						</div>
						{parsedAmount > balanceNumber && (
							<p className="text-red-500 text-sm">Insufficient balance</p>
						)}
					</div>

					{/* Submit Button */}
					<button
						className="flex w-full items-center justify-center gap-2 rounded-xl bg-text-primary px-4 py-3.5 font-semibold text-bg-primary transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-primary focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
						disabled={!canSubmit}
						onClick={handleWithdraw}
						type="button"
					>
						{isPending ? (
							<>
								<Loader2 aria-hidden="true" className="size-5 animate-spin" />
								<span>{getStatusMessage()}</span>
							</>
						) : isBelowMinimum ? (
							<span>Minimum $1</span>
						) : (
							<span>Withdraw</span>
						)}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
